import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines -- Login page orchestrates multiple auth flows (magic link, password, forgot password, confirmations) and composes desktop/mobile layouts */
import { Box, Button, CircularProgress, Link } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";
import { verifyEmailOtp } from "@src/appV2/Auth/cognito/emailOtpAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { clearMagicLinkEmail } from "@src/appV2/Auth/cognito/magicLinkEmailStore";
import { formatSessionInitErrorMessage } from "@src/appV2/Auth/cognito/formatSessionInitErrorMessage";
import {
  initCognitoSession,
  SESSION_INIT_OUTCOME,
} from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import {
  clearRedirectInProgress,
  hasCognitoTokens,
  isRedirectInProgress,
  markRedirectInProgress,
} from "@src/appV2/Auth/cognito/redirectInProgress";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  isConditionalPasskeySignInAvailable,
  isPasskeySupported,
  signInWithPasskey,
} from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getRememberedEmail } from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { requestRememberEmail } from "@src/appV2/Auth/rememberEmail/requestRememberEmail";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logPasswordLoginFailure,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OutageBanner } from "@src/appV2/OutageBanner/OutageBanner";
import { useOutageBanner } from "@src/appV2/OutageBanner/useOutageBanner";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import {
  completeNewPasswordChallenge,
  loginWithEmailAndPassword,
} from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendEmailOtp } from "@src/mobile/Login/sendEmailOtp";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";

import { useHistory, useLocation } from "react-router-dom";
import loginIllustration from "./assets/login-illustration.webp";
import { DesktopLoginLayout } from "./DesktopLoginLayout";
import { ForceChangePasswordForm } from "./ForceChangePasswordForm";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { MobileLoginLayout } from "./MobileLoginLayout";
import { OtpEntryForm } from "./OtpEntryForm";
import { useResendCooldown } from "./useResendCooldown";
import { useSsoLogin } from "./useSsoLogin";
const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";
type LoginMode =
  | "magicLink"
  | "password"
  | "forgotPassword"
  | "resetEmailSent"
  | "forceChangePassword";

const PASSWORD_MIN_LENGTH = 12;
const CONDITIONAL_PASSKEY_FALLBACK_MS = 3000;
// Keep aligned with cbh-infrastructure/authentication/modules/app_client/main.tf:
// Cognito app-client AuthSessionValidity is configured in minutes, and Amplify
// does not expose the server-side expiry timestamp to this screen.
const EMAIL_OTP_AUTH_SESSION_VALIDITY_SECONDS = 15 * 60;

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "magicLink",
  "password",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "magicLink";
}

/**
 * Clears stale OAuth/PKCE state from previous failed SSO attempts so the
 * login page starts clean. Only clears OAuth flow state — not Cognito
 * tokens or session data, which are managed by the logout flow.
 */
function clearStaleAuthState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState") || key?.includes("oauthPKCE")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

export function Login(): ReactElement {
  const { tu } = useTu("login");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const history = useHistory();
  const location = useLocation();
  // Pre-fill the email remembered on this device, if the user opted in before.
  const [email, setEmail] = useState(() => getRememberedEmail() ?? "");
  // Cognito username resolved by the initial OTP send, reused on resend so
  // unverified email-alias users (whose email alias can't initiate auth) can
  // still request a fresh code.
  const resolvedOtpUsernameRef = useRef<string | undefined>(undefined);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Whether the passwordless flow has advanced to its second step: the OTP
  // code-entry form when the email-OTP flag is on, or the "magic link sent"
  // confirmation when it's off.
  const [passwordlessRequested, setPasswordlessRequested] = useState(false);
  // Set once any login flow succeeds and we start navigating to the dashboard,
  // so we swap to a full-page "signing in" state instead of any login form.
  // Initialized from the cross-mount signal so a remount triggered by the
  // post-login session-hydration bounce shows the spinner, not the form.
  const [isRedirecting, setIsRedirecting] = useState(isRedirectInProgress);

  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );

  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();
  // Forced password change (Cognito FORCE_CHANGE_PASSWORD / temporary password).
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [forceChangeError, setForceChangeError] = useState<string | undefined>();
  const [passwordlessError, setPasswordlessError] = useState<string | undefined>();
  const [isOtpSessionUnavailable, setIsOtpSessionUnavailable] = useState(false);
  const [hasActiveOtpCode, setHasActiveOtpCode] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | undefined>();
  // Guards the chooser's SSO button so rapid clicks can't fire multiple redirects.
  const [chooserSsoLoading, setChooserSsoLoading] = useState(false);
  const conditionalPasskeyAbortControllerRef = useRef<AbortController | undefined>(undefined);

  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });
  // When on, the passwordless flow sends a 6-digit email OTP and prompts for the
  // code; when off (default), it falls back to the original magic-link email.
  const isEmailOtpEnabled = useCbhFlag(CbhFeatureFlag.EMAIL_OTP_INSTEAD_OF_MAGIC_LINK, {
    defaultValue: false,
  });
  // When on, the email-first / password-default redesigned workplace login.
  const isRedesignedLoginEnabled = useCbhFlag(CbhFeatureFlag.REDESIGNED_WORKPLACE_LOGIN_FLOW, {
    defaultValue: false,
  });
  const passkeySupported = isPasskeySupported();
  const isPasskeySignInAvailable = isPasskeyAuthEnabled && passkeySupported;

  const abortConditionalPasskeyRequest = useCallback(() => {
    conditionalPasskeyAbortControllerRef.current?.abort();
    conditionalPasskeyAbortControllerRef.current = undefined;
  }, []);

  useEffect(() => {
    return () => {
      abortConditionalPasskeyRequest();
    };
  }, [abortConditionalPasskeyRequest]);

  // Clear stale auth state when the login page mounts. Previous failed
  // SSO attempts or stale Cognito tokens can leave state that causes
  // infinite loading loops or redirect errors on retry.
  useEffect(() => {
    clearStaleAuthState();
    // Drop a leftover redirect signal when there are no Cognito tokens — that
    // means we're not mid-login (e.g. landed here after logout), so show the
    // form rather than a perpetual spinner.
    if (!hasCognitoTokens()) {
      clearRedirectInProgress();
      setIsRedirecting(false);
    }
  }, []);

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);
  const enableWebAuthnAutocomplete = isPasskeySignInAvailable && ssoMode !== "sso_only";
  const { secondsRemaining, isCoolingDown, startCooldown, clearCooldown } = useResendCooldown();
  const {
    secondsRemaining: codeSecondsRemaining,
    startCooldown: startCodeValidityCountdown,
    clearCooldown: clearCodeValidityCountdown,
  } = useResendCooldown(EMAIL_OTP_AUTH_SESSION_VALIDITY_SECONDS);
  const isOtpCodeExpired = hasActiveOtpCode && codeSecondsRemaining === 0;
  const noAssociatedWorkplacesMessage = tu(
    "Your account is active, but it isn't linked to an active workplace. Ask a workplace administrator to link your account to a workplace, then try again."
  );

  // Enters the "signing in" state and persists the cross-mount signal. MUST be
  // called BEFORE `dispatch(initCognitoSession())`: that dispatch flips
  // `isAuthorized` mid-await, which remounts <Login>; the signal has to already
  // be set so the fresh instance shows the spinner instead of flashing the form.
  const beginRedirect = useCallback(() => {
    markRedirectInProgress();
    setIsRedirecting(true);
  }, []);

  // Reverts the "signing in" state when a login that started redirecting fails
  // before it navigates (e.g. session hydration fails), so the form returns.
  const cancelRedirect = useCallback(() => {
    clearRedirectInProgress();
    setIsRedirecting(false);
  }, []);

  // Navigates to the post-login destination. The "signing in" state is already
  // in effect (see `beginRedirect`), so no login form renders during the
  // redirect. Shared by every success path (email OTP, password, passkey).
  const redirectAfterLogin = useCallback(() => {
    const storedRedirectUrl = localStorage.getItem("redirectURL");
    const redirectUrl =
      storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
    localStorage.removeItem("redirectURL");
    history.replace(redirectUrl);
    // Clear the shared signal now that the hand-off has happened — the bounce
    // remount already occurred during session hydration (before this runs), and
    // we're now navigating away from /login. Leaving it set would let a later
    // same-session AuthRedirect visit short-circuit straight to the dashboard
    // (its shortcut treats the flag + live Cognito tokens as "redirect in flight").
    clearRedirectInProgress();
  }, [history]);

  const completePasskeyLogin = useCallback(
    async ({
      submittedEmail,
      setError,
    }: {
      submittedEmail: string;
      setError: (message: string) => void;
    }) => {
      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      const sessionResult = await dispatch(initCognitoSession({ authMethod: "passkey" }));
      if (sessionResult.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setError(
          tu(
            'Your passkey was verified, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        return true;
      }
      if (sessionResult.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        setError(
          formatSessionInitErrorMessage(sessionResult.error, {
            networkFailureMessage: tu(
              "We couldn't reach Clipboard's servers. Check your internet connection and try again."
            ),
            noAssociatedWorkplacesMessage,
            defaultMessage: tu(
              "We verified your passkey, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            ),
          })
        );
        return true;
      }

      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
      // Request the "remember your email?" prompt only after hydration
      // succeeds (see handleVerifyOtp) so a failed login never leaves the
      // focus-trapping dialog stuck over the form.
      requestRememberEmail(submittedEmail);
      redirectAfterLogin();
      return true;
    },
    [dispatch, beginRedirect, cancelRedirect, redirectAfterLogin, noAssociatedWorkplacesMessage, tu]
  );

  // Requests an email OTP for the current email and starts the resend cooldown
  // on success. Shared by the initial submit and the code-entry resend; returns
  // whether the request succeeded so callers can switch to the code-entry view.
  const sendOtp = useCallback(
    async (options?: { isResend?: boolean }) => {
      abortConditionalPasskeyRequest();
      setLoading(true);
      setPasswordlessError(undefined);

      const isResend = options?.isResend ?? false;
      const result = await sendEmailOtp({
        email,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526). Instead it reuses the
        // username the initial send resolved, so users whose email alias can't
        // initiate auth still get a fresh code.
        skipEnsureUser: isResend,
        username: isResend ? resolvedOtpUsernameRef.current : undefined,
        // Redesigned flow: if the account advertises a usable password, prefer
        // the password prompt over sending a code. Only on the initial send,
        // which resolves capabilities via the PreSignUp round trip.
        preferPasswordIfUsable: !isResend && isRedesignedLoginEnabled,
      });

      if (result.success) {
        resolvedOtpUsernameRef.current = result.username;
        setIsOtpSessionUnavailable(false);
        setHasActiveOtpCode(true);
        startCodeValidityCountdown();
        startCooldown();
      } else if (result.requiresPassword) {
        setIsOtpSessionUnavailable(false);
        // Route to the password form instead of dead-ending on the email step:
        // either email-code sign-in isn't available (password-only, or mid
        // forced-password-change), or the redesigned flow prefers the account's
        // usable password. Carry any explanation across (none for the preference).
        setLoginError(result.error);
        setLoginMode("password");
        setLoading(false);
        return false;
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [
      abortConditionalPasskeyRequest,
      email,
      isRedesignedLoginEnabled,
      startCodeValidityCountdown,
      startCooldown,
    ]
  );

  const attemptConditionalPasskeySignIn = useCallback(
    async ({ submittedEmail }: { submittedEmail: string }) => {
      if (!enableWebAuthnAutocomplete) {
        return false;
      }

      const isConditionalAvailable = await isConditionalPasskeySignInAvailable();
      if (!isConditionalAvailable) {
        return false;
      }

      abortConditionalPasskeyRequest();
      const abortController = new AbortController();
      conditionalPasskeyAbortControllerRef.current = abortController;
      const fallbackTimer = window.setTimeout(() => {
        abortController.abort();
      }, CONDITIONAL_PASSKEY_FALLBACK_MS);

      setPasskeyError(undefined);
      setPasswordlessError(undefined);
      setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, {
        hasEmail: true,
        mediation: "conditional",
      });

      try {
        const result = await signInWithPasskey(submittedEmail, {
          mediation: "conditional",
          signal: abortController.signal,
        });

        if (result.status !== "signed-in") {
          if (result.status === "no-passkey") {
            logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL, {
              mediation: "conditional",
            });
          }
          setLoginIdentityProvider(undefined);
          return false;
        }

        return await completePasskeyLogin({
          submittedEmail,
          setError: setPasswordlessError,
        });
      } catch (error: unknown) {
        setLoginIdentityProvider(undefined);
        if (abortController.signal.aborted) {
          return false;
        }

        const mapped = mapCognitoError(error, "passkey");
        logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
          error,
          metadata: { errorCode: mapped.code, mediation: "conditional" },
        });
        return false;
      } finally {
        window.clearTimeout(fallbackTimer);
        if (conditionalPasskeyAbortControllerRef.current === abortController) {
          conditionalPasskeyAbortControllerRef.current = undefined;
        }
      }
    },
    [abortConditionalPasskeyRequest, completePasskeyLogin, enableWebAuthnAutocomplete]
  );

  // Sends the magic link to the current email and starts the resend cooldown on
  // success. Shared by the initial submit and the confirmation-screen resend;
  // returns whether the send succeeded so callers can react (e.g. show the
  // confirmation screen).
  const sendMagicLink = useCallback(
    async (options?: { isResend?: boolean }) => {
      abortConditionalPasskeyRequest();
      setLoading(true);
      setPasswordlessError(undefined);

      const result = await sendLoginEmail({
        email,
        requestedURL: localStorage.getItem("redirectURL") ?? undefined,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [abortConditionalPasskeyRequest, email, startCooldown]
  );

  const handlePasswordlessSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    const submittedEmail = email.trim();
    setPasskeyError(undefined);

    const didSignInWithPasskey = await attemptConditionalPasskeySignIn({ submittedEmail });
    if (didSignInWithPasskey) {
      return;
    }

    // Production: SSO replaces the email flow for CBH employees
    if (ssoMode === "sso_only") {
      abortConditionalPasskeyRequest();
      setLoading(true);
      setPasswordlessError(undefined);
      await handleSsoRedirect("submit");
      setLoading(false);
      return;
    }

    const requested = isEmailOtpEnabled ? await sendOtp() : await sendMagicLink();
    if (requested) {
      setPasswordlessRequested(true);
    }
  }, [
    email,
    attemptConditionalPasskeySignIn,
    ssoMode,
    abortConditionalPasskeyRequest,
    handleSsoRedirect,
    isEmailOtpEnabled,
    sendOtp,
    sendMagicLink,
  ]);

  // Resends the magic link to the same email from the confirmation screen.
  // Gated by a cooldown so users can't spam the endpoint; the cooldown
  // restarts on every successful resend. Magic-link flow only.
  const handleResendEmail = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendMagicLink({ isResend: true });
  }, [email, isCoolingDown, sendMagicLink]);

  // Resends the email OTP to the same address from the code-entry screen. Gated
  // by the same cooldown as the magic link resend so users can't spam the
  // endpoint; the cooldown restarts on every successful resend. Email-OTP flow
  // only.
  const handleResendOtp = useCallback(async () => {
    if (!email || (isCoolingDown && !isOtpSessionUnavailable)) {
      return;
    }

    await sendOtp({ isResend: true });
  }, [email, isCoolingDown, isOtpSessionUnavailable, sendOtp]);

  // Verifies the code typed on this (the requesting) browser. A single wrong
  // code fails the Cognito flow — the user can re-enter the code, resend a new
  // one, or go back.
  const handleVerifyOtp = useCallback(
    async (code: string) => {
      abortConditionalPasskeyRequest();
      if (isOtpCodeExpired) {
        clearCooldown();
        setIsOtpSessionUnavailable(true);
        setPasswordlessError(tu('This code has expired. Tap "Resend code" to get a new code.'));
        return;
      }

      setLoading(true);
      setPasswordlessError(undefined);

      try {
        await verifyEmailOtp(code);

        // Enter the redirect state BEFORE hydrating the session — that dispatch
        // remounts <Login>, and the signal must already be set so the remount
        // shows the spinner rather than flashing the login form.
        beginRedirect();
        const result = await dispatch(initCognitoSession());
        if (result.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
          // Worker signed in at the workplace/admin login — friendly redirect
          // copy, not the misleading "session not restored" retry message.
          cancelRedirect();
          setPasswordlessError(
            tu(
              'The submitted code was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
            )
          );
          setLoading(false);
          return;
        }
        if (result.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
          cancelRedirect();
          // A transient network drop on the post-OTP hydration call is not a
          // defect — the OTP itself succeeded and the user just retries. Match
          // the catch block and initCognitoSession's own classification so it
          // stays out of Error Tracking and the rollout dashboard's defect count.
          const { outcome, reason } = isNetworkFailure(result.error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : {
                outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
                reason: LOGIN_REASON.SESSION_HYDRATION_FAILED,
              };
          logPasswordLoginFailure({
            eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
            error:
              result.error ??
              new Error("Cognito session not restored after email OTP verification"),
            metadata: { outcome, reason },
          });
          setPasswordlessError(
            formatSessionInitErrorMessage(result.error, {
              networkFailureMessage: tu(
                "We couldn't reach Clipboard's servers. Check your internet connection and try again."
              ),
              noAssociatedWorkplacesMessage,
              defaultMessage: tu(
                "We verified your code, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
              ),
            })
          );
          setLoading(false);
          return;
        }

        logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });

        // Request the "remember your email?" prompt only now that hydration
        // succeeded. Requested earlier, it opens a focus-trapping dialog over
        // the auth page; if login then fails (worker account, hydration error),
        // the dialog stays stuck open and freezes the form, and its pending flag
        // re-opens it on reload.
        requestRememberEmail(email);

        clearMagicLinkEmail();
        redirectAfterLogin();
      } catch (error: unknown) {
        cancelRedirect();
        const mapped = mapCognitoError(error);
        // Classify so expected friction (wrong/expired code) is recorded as a
        // Datadog action while genuine defects keep the error attached and
        // reach Error Tracking, matching the password-login pattern.
        const { outcome, reason } =
          mapped.code === "auth/unknown" && isNetworkFailure(error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mapped.code);
        logPasswordLoginFailure({
          eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
          error,
          metadata: { errorCode: mapped.code, errorMessage: mapped.message, outcome, reason },
        });
        if (mapped.code === "auth/missing-sign-in-state") {
          clearCooldown();
          clearCodeValidityCountdown();
          setHasActiveOtpCode(false);
          setIsOtpSessionUnavailable(true);
          setPasswordlessError(
            tu('Your sign-in session is no longer available. Tap "Resend code" to get a new code.')
          );
          setLoading(false);
          return;
        }
        // A wrong/expired native EMAIL_OTP surfaces as CodeMismatchException
        // (auth/invalid-code) or ExpiredCodeException (auth/expired-code); an
        // expired sign-in session (NotAuthorizedException "session is expired")
        // also maps to auth/expired-code. Network/unknown failures map to
        // auth/unknown. Show the OTP-specific retry copy for all of them so users
        // don't get magic-link/reset guidance.
        const isOtpRetryable =
          mapped.code === "auth/unknown" ||
          mapped.code === "auth/invalid-code" ||
          mapped.code === "auth/expired-code";
        setPasswordlessError(
          isOtpRetryable
            ? tu("That code is incorrect or expired. Go back and request a new code.")
            : mapped.message
        );
        setIsOtpSessionUnavailable(false);
        setLoading(false);
      }
    },
    [
      email,
      dispatch,
      beginRedirect,
      cancelRedirect,
      redirectAfterLogin,
      isOtpCodeExpired,
      clearCooldown,
      clearCodeValidityCountdown,
      abortConditionalPasskeyRequest,
      noAssociatedWorkplacesMessage,
      tu,
    ]
  );

  useEffect(() => {
    if (!isOtpCodeExpired || loading) {
      return;
    }

    clearCooldown();
    setIsOtpSessionUnavailable(true);
    setPasswordlessError(tu('This code has expired. Tap "Resend code" to get a new code.'));
  }, [isOtpCodeExpired, loading, clearCooldown, tu]);

  const handlePasswordSubmit = useCallback(async () => {
    abortConditionalPasskeyRequest();
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    const result = await loginWithEmailAndPassword({ email, password });

    // The temporary password was accepted but Cognito requires a replacement
    // before sign-in completes. Switch to the forced password-change screen,
    // keeping the in-memory Amplify challenge alive for `confirmSignIn`.
    if (!result.success && result.requiresNewPassword) {
      setNewPassword("");
      setConfirmNewPassword("");
      setForceChangeError(undefined);
      setLoginMode("forceChangePassword");
      setLoading(false);
      return;
    }

    if (result.success) {
      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const result = await dispatch(initCognitoSession());
      if (result.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setLoginError(
          tu(
            'Your password was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        setLoading(false);
        return;
      }
      if (result.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        setLoginError(
          formatSessionInitErrorMessage(result.error, {
            networkFailureMessage: tu(
              "We couldn't reach Clipboard's servers. Check your internet connection and try again."
            ),
            noAssociatedWorkplacesMessage,
            defaultMessage: tu(
              "We signed you in, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            ),
          })
        );
        setLoading(false);
        return;
      }

      // Request the "remember your email?" prompt only after hydration
      // succeeds (see handleVerifyOtp) so a failed login never leaves the
      // focus-trapping dialog stuck over the form.
      requestRememberEmail(email);

      redirectAfterLogin();
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [
    abortConditionalPasskeyRequest,
    email,
    password,
    dispatch,
    beginRedirect,
    cancelRedirect,
    redirectAfterLogin,
    noAssociatedWorkplacesMessage,
    tu,
  ]);

  // Completes the forced password change: validates the new password locally,
  // responds to the pending Cognito challenge, then hydrates the session exactly
  // like the password path. Local policy/mismatch errors surface inline without
  // a round-trip; Cognito policy rejections come back as auth/invalid-password.
  const handleForceChangePasswordSubmit = useCallback(async () => {
    abortConditionalPasskeyRequest();
    if (newPassword !== confirmNewPassword) {
      setForceChangeError(tu("Passwords do not match."));
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setForceChangeError(
        tu("Password must be at least {{minLength}} characters.", {
          values: { minLength: PASSWORD_MIN_LENGTH },
        })
      );
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)) {
      setForceChangeError(
        tu("Password must include at least one uppercase and one lowercase letter.")
      );
      return;
    }
    // Unlike ChangePassword/ConfirmForgotPassword, Cognito does NOT validate the
    // NEW_PASSWORD sent in the RespondToAuthChallenge map against its
    // no-edge-whitespace rule (^[\S]+.*[\S]+$). Reject it here so a password with
    // a leading/trailing space can't be stored — that keeps every credential
    // trimmable-safe at sign-in. We reject rather than trim: the new password is
    // the user's deliberate choice and must not be silently altered.
    if (newPassword !== newPassword.trim()) {
      setForceChangeError(tu("Your new password can't start or end with a space."));
      return;
    }

    setLoading(true);
    setForceChangeError(undefined);

    const result = await completeNewPasswordChallenge({ newPassword });

    if (!result.success) {
      setForceChangeError(result.error);
      setLoading(false);
      return;
    }

    // Enter the redirect state BEFORE hydrating the session — that dispatch
    // remounts <Login>, and the signal must already be set so the remount shows
    // the spinner rather than flashing a form.
    beginRedirect();
    const sessionResult = await dispatch(initCognitoSession());
    if (sessionResult.outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
      cancelRedirect();
      setForceChangeError(
        tu(
          'Your password was set, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
        )
      );
      setLoading(false);
      return;
    }
    if (sessionResult.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
      cancelRedirect();
      setForceChangeError(
        formatSessionInitErrorMessage(sessionResult.error, {
          networkFailureMessage: tu(
            "We couldn't reach Clipboard's servers. Check your internet connection and try again."
          ),
          noAssociatedWorkplacesMessage,
          defaultMessage: tu(
            "We set your password, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
          ),
        })
      );
      setLoading(false);
      return;
    }

    requestRememberEmail(email);
    redirectAfterLogin();
  }, [
    newPassword,
    confirmNewPassword,
    email,
    dispatch,
    beginRedirect,
    cancelRedirect,
    abortConditionalPasskeyRequest,
    redirectAfterLogin,
    noAssociatedWorkplacesMessage,
    tu,
  ]);

  // Reaches `signInWithPasskey` synchronously from the click (no prior await)
  // so the WebAuthn ceremony stays inside the user gesture.
  const handlePasskeyClick = useCallback(async () => {
    abortConditionalPasskeyRequest();
    if (!email) {
      return;
    }

    setPasskeyLoading(true);
    setPasskeyError(undefined);
    setPasswordlessError(undefined);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
    logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, { hasEmail: Boolean(email) });

    // Cognito's user-existence-error prevention fabricates a challenge for
    // unknown emails (sometimes factor selection → "no-passkey", sometimes a
    // real WEB_AUTHN ceremony, sometimes a NotAuthorized/UserNotFound error), so
    // the client genuinely can't tell "no account", "account without a passkey",
    // and "wrong passkey" apart. Show one honest message for every passkey
    // failure instead of leaking a misleading user-existence claim.
    const passkeyUnavailableMessage = tu(
      "We couldn't sign you in with a passkey for this email. If you have an account, sign in with a link or password, then add a passkey from the My Account page."
    );

    try {
      const result = await signInWithPasskey(email.trim());

      if (result.status === "no-passkey") {
        logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL);
        setPasskeyError(passkeyUnavailableMessage);
        setPasskeyLoading(false);
        return;
      }

      await completePasskeyLogin({
        submittedEmail: email.trim(),
        setError: setPasskeyError,
      });
      setPasskeyLoading(false);
    } catch (error: unknown) {
      cancelRedirect();
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      // A user-not-found error here is just prevention noise — the email may
      // well be valid. Collapse it into the neutral message; keep genuine
      // passkey errors (cancelled, security, unsupported) actionable.
      setPasskeyError(
        mapped.code === "auth/user-not-found" ? passkeyUnavailableMessage : mapped.message
      );
      setPasskeyLoading(false);
    }
  }, [abortConditionalPasskeyRequest, completePasskeyLogin, email, cancelRedirect, tu]);

  const handleForgotPassword = useCallback(async () => {
    abortConditionalPasskeyRequest();
    if (!email) {
      setResetError(tu("Please enter your email address first."));
      return;
    }

    setLoading(true);
    setResetError(undefined);

    try {
      // No ensureCognitoUser JIT here: a password reset only applies to a user
      // who already exists in Cognito with a password. JIT-creating one would
      // make a duplicate, email_verified=false account whose reset is blocked
      // anyway. JIT belongs in first-login (magic link / OTP), not reset.
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logPasswordResetRequestFailure({ error, metadata: { errorCode: mapped.code } });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [abortConditionalPasskeyRequest, email, tu]);

  // Clear any stale passkey error when the email changes, so a "no passkey for
  // this email" message doesn't linger against a different address.
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setPasskeyError(undefined);
  }, []);

  const isDesktop = useIsDesktop();
  const outageBanner = useOutageBanner();
  const banner = outageBanner.isVisible ? <OutageBanner /> : undefined;

  // While redirecting, hide every login affordance (illustration, heading, and
  // all forms) so only the "signing in" indicator shows during the hand-off.
  const showIllustration =
    !isRedirecting &&
    !passwordlessRequested &&
    loginMode !== "resetEmailSent" &&
    loginMode !== "forceChangePassword";

  // Entry step: the redesigned email-only step, or the legacy magic-link email form.
  const isEntryStep = loginMode === "magicLink" && !passwordlessRequested;

  const showWelcomeHeading =
    !isRedirecting &&
    ((loginMode === "magicLink" && !passwordlessRequested) || loginMode === "password");

  const signingInBox = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        paddingY: 8,
      }}
    >
      <CircularProgress />
      <Text variant="body1" sx={{ color: "base.muted" }}>
        {tu("Signing you in…")}
      </Text>
    </Box>
  );

  // Redesigned flow: the alternative sign-in methods, shown as prominent brand
  // buttons beneath the just-sent link/code so users who can't access their
  // email aren't forced down the passwordless path. Password opens the password
  // step; passkey and SSO are offered when available.
  const redesignedAlternatives = isRedesignedLoginEnabled ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      {/* Divider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
        <Text variant="body2" sx={{ color: "base.inactive" }}>
          {tu("or")}
        </Text>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
      </Box>

      <Button
        fullWidth
        variant="primary"
        size="large"
        disabled={loading || passkeyLoading}
        sx={{ textTransform: "none" }}
        onClick={() => {
          abortConditionalPasskeyRequest();
          setPasswordlessError(undefined);
          setPasskeyError(undefined);
          setLoginMode("password");
        }}
      >
        {tu("Sign in with password")}
      </Button>

      {isPasskeySignInAvailable && (
        <LoadingButton
          fullWidth
          variant="primary"
          size="large"
          isLoading={passkeyLoading}
          disabled={loading}
          onClick={() => {
            void handlePasskeyClick();
          }}
        >
          {tu("Sign in with a passkey")}
        </LoadingButton>
      )}

      {ssoMode === "sso_and_magic_link" && (
        <LoadingButton
          fullWidth
          variant="primary"
          size="large"
          isLoading={chooserSsoLoading}
          disabled={loading || passkeyLoading}
          onClick={() => {
            abortConditionalPasskeyRequest();
            setChooserSsoLoading(true);
            void handleSsoRedirect("sso_button").finally(() => {
              setChooserSsoLoading(false);
            });
          }}
        >
          {tu("Sign in with SSO")}
        </LoadingButton>
      )}

      {passkeyError && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {passkeyError}
        </Text>
      )}
    </Box>
  ) : null;

  const formBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {/* Redesigned: email-only first step. Continue sends the magic link / email
          OTP straight away (or redirects to SSO when enforced); the alternative
          sign-in methods are then offered alongside the sent-link/code screen. */}
      {isRedesignedLoginEnabled && isEntryStep && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          errorMessage={passwordlessError}
          submitLabelOverride={tu("Continue")}
          enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
          setEmail={handleEmailChange}
          onSubmit={handlePasswordlessSubmit}
        />
      )}

      {!isRedesignedLoginEnabled && isEntryStep && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          showSsoButton={ssoMode === "sso_and_magic_link"}
          submitLabelOverride={isEmailOtpEnabled ? tu("Get Sign-in Code") : tu("Get Sign-in Link")}
          errorMessage={passwordlessError}
          showPasskeyButton={enableWebAuthnAutocomplete}
          enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
          passkeyLoading={passkeyLoading}
          passkeyError={passkeyError}
          setEmail={handleEmailChange}
          onSsoClick={() => {
            abortConditionalPasskeyRequest();
            return handleSsoRedirect("sso_button");
          }}
          onSubmit={handlePasswordlessSubmit}
          onPasskeyClick={() => {
            void handlePasskeyClick();
          }}
          onPasskeyInvalidEmail={() => {
            setPasskeyError(tu("Please enter a valid email address to sign in with a passkey."));
          }}
          onSwitchToPassword={() => {
            abortConditionalPasskeyRequest();
            setPasswordlessError(undefined);
            setPasskeyError(undefined);
            setLoginMode("password");
          }}
        />
      )}

      {passwordlessRequested && isEmailOtpEnabled && loginMode === "magicLink" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
          <OtpEntryForm
            email={email}
            loading={loading}
            errorMessage={passwordlessError}
            isResendCoolingDown={isCoolingDown && !isOtpSessionUnavailable}
            resendSecondsRemaining={secondsRemaining}
            isResendPrioritized={isOtpSessionUnavailable}
            codeSecondsRemaining={codeSecondsRemaining}
            isCodeExpired={isOtpCodeExpired}
            isSubmitBlocked={isOtpCodeExpired || isOtpSessionUnavailable}
            onSubmit={handleVerifyOtp}
            onResend={() => {
              void handleResendOtp();
            }}
            onBack={() => {
              setPasswordlessError(undefined);
              setIsOtpSessionUnavailable(false);
              setHasActiveOtpCode(false);
              clearCodeValidityCountdown();
              setPasswordlessRequested(false);
            }}
          />
          {redesignedAlternatives}
        </Box>
      )}

      {passwordlessRequested && !isEmailOtpEnabled && loginMode === "magicLink" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Verification link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("We have sent an email with a login link to {{email}}.", {
              values: { email },
            })}
          </Text>
          {passwordlessError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {passwordlessError}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              isLoading={loading}
              disabled={isCoolingDown}
              onClick={() => {
                void handleResendEmail();
              }}
            >
              {isCoolingDown
                ? tu("Resend email in {{seconds}}s", { values: { seconds: secondsRemaining } })
                : tu("Resend email")}
            </LoadingButton>
            <Button
              variant="text"
              disabled={loading}
              sx={{ textTransform: "none", color: "primary.main" }}
              onClick={() => {
                setPasswordlessError(undefined);
                setPasswordlessRequested(false);
              }}
            >
              {tu("Go back to login page")}
            </Button>
          </Box>
          {redesignedAlternatives}
        </Box>
      )}

      {loginMode === "password" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <LoginWithEmailAndPasswordForm
            email={email}
            password={password}
            loading={loading}
            errorMessage={loginError}
            hideEmailField={isRedesignedLoginEnabled}
            showSetResetGuidance={isRedesignedLoginEnabled}
            enableWebAuthnAutocomplete={enableWebAuthnAutocomplete}
            setEmail={setEmail}
            setPassword={setPassword}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={() => {
              abortConditionalPasskeyRequest();
              setLoginError(undefined);
              setLoginMode("forgotPassword");
            }}
            onChangeEmail={() => {
              abortConditionalPasskeyRequest();
              setLoginError(undefined);
              setPasswordlessError(undefined);
              setPassword("");
              setPasswordlessRequested(false);
              setLoginMode("magicLink");
            }}
          />

          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              gap: 5,
              marginTop: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
          </Box>

          {isRedesignedLoginEnabled ? (
            <Button
              fullWidth
              variant="primary"
              size="large"
              disabled={loading}
              sx={{ textTransform: "none", marginTop: 4 }}
              onClick={() => {
                abortConditionalPasskeyRequest();
                setLoginError(undefined);
                setPasskeyError(undefined);
                setLoginMode("magicLink");
              }}
            >
              {tu("Try another way to sign in")}
            </Button>
          ) : (
            <Button
              variant="text"
              disabled={loading}
              sx={{
                textTransform: "none",
                color: "primary.main",
                marginTop: 4,
                alignSelf: "center",
              }}
              onClick={() => {
                abortConditionalPasskeyRequest();
                setLoginMode("magicLink");
              }}
            >
              {tu("Sign in with link")}
            </Button>
          )}
        </Box>
      )}

      {loginMode === "forceChangePassword" && (
        <ForceChangePasswordForm
          email={email}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmNewPassword}
          setConfirmPassword={setConfirmNewPassword}
          loading={loading}
          errorMessage={forceChangeError}
          onSubmit={handleForceChangePasswordSubmit}
          onCancel={() => {
            abortConditionalPasskeyRequest();
            setForceChangeError(undefined);
            setNewPassword("");
            setConfirmNewPassword("");
            setPassword("");
            // Clear the passwordless step so the redesigned flow returns to the
            // email entry step, not the prior sent-link / OTP screen.
            setPasswordlessRequested(false);
            setLoginMode(isRedesignedLoginEnabled ? "magicLink" : "password");
          }}
        />
      )}

      {loginMode === "forgotPassword" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            width: "100%",
          }}
        >
          <Text bold variant="body1" sx={{ textAlign: "center" }}>
            {tu("Set/reset password")}
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("Enter your email address and we'll send you a link to set/reset your password.")}
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Email")}
            </Text>
            <TextInput
              fullWidth
              required
              type="email"
              placeholder="your.email@example.com"
              value={email}
              disabled={loading}
              autoComplete="email"
              inputProps={{ "aria-label": "Email" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Box>
          {resetError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {resetError}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={loading}
            disabled={!email}
            onClick={() => {
              void handleForgotPassword();
            }}
          >
            {tu("Send Set/Reset Link")}
          </LoadingButton>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={() => {
              abortConditionalPasskeyRequest();
              setResetError(undefined);
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {loginMode === "resetEmailSent" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Reset link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu(
              "We have sent a password reset link to {{email}}. Check your email and follow the instructions.",
              { values: { email } }
            )}
          </Text>
          <Button
            variant="text"
            sx={{ textTransform: "none", color: "primary.main" }}
            onClick={() => {
              abortConditionalPasskeyRequest();
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {showIllustration && (
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {tu("Don't have a workplace account?")}
          </Text>
          <Link
            href={WORKPLACE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
            }}
          >
            {tu("Sign up")}
          </Link>
        </Box>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout banner={banner}>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {showWelcomeHeading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Text
                variant="h5"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1.2,
                  color: "base.base",
                  textAlign: "center",
                }}
              >
                {tu("Welcome!")}
              </Text>
            </Box>
          )}
          {isRedirecting ? signingInBox : formBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout banner={banner}>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {isRedirecting ? signingInBox : formBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
